import { useMemo } from 'react';
import { useShowContext } from 'framework/show';

interface ValidationSchema {
  validate: (data: unknown, options?: { abortEarly?: boolean }) => Promise<unknown>;
}

interface UseModalHandlerProps {
  onClose: () => void;
  afterSubmit?: { execute: (data?: unknown) => void };
  submitFormFunc?: (data: any) => Promise<any>;
  getValidationSchemaFunc?: () => Promise<ValidationSchema> | ValidationSchema;
  contextDataUpdater?: (response: any, contextData: any) => any;
}

const useModalHandler = ({
  onClose,
  afterSubmit,
  submitFormFunc,
  getValidationSchemaFunc,
  contextDataUpdater,
}: UseModalHandlerProps) => {
  const { data: contextData, setData } = useShowContext();

  const handleAfterSubmit = useMemo(() => {
    if (!afterSubmit?.execute && !contextDataUpdater) {
      return {
        execute: () => {
          onClose();
        },
      };
    }

    return {
      execute: (response: any) => {
        if (contextDataUpdater && setData && contextData) {
          const updatedData = contextDataUpdater(response, contextData);
          setData(updatedData);
        }

        if (afterSubmit?.execute) {
          afterSubmit.execute(response);
        } else {
          onClose();
        }
      },
    };
  }, [afterSubmit, setData, contextData, onClose, contextDataUpdater]);

  const handleFormSubmit = useMemo(() => {
    return submitFormFunc || (() => Promise.resolve());
  }, [submitFormFunc]);

  return {
    handleAfterSubmit,
    handleFormSubmit,
    getValidationSchemaFunc,
  };
};

export default useModalHandler;
export type { ValidationSchema };
